// Supabase-based data management utilities for production use
import { supabase, TABLES, isSupabaseConfigured } from '../lib/supabase';

/**
 * Fetch all hotel data from Supabase
 */
export const fetchHotelDataFromDB = async () => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Supabase not configured' };
  }

  try {
    // Fetch all data in parallel
    const [
      settingsResult,
      sectionsResult, 
      itemsResult,
      hoursResult,
      eventsResult,
      contactResult
    ] = await Promise.all([
      supabase.from(TABLES.HOTEL_SETTINGS).select('*').single(),
      supabase.from(TABLES.MENU_SECTIONS).select('*').order('display_order'),
      supabase.from(TABLES.MENU_ITEMS).select('*'),
      supabase.from(TABLES.OPENING_HOURS).select('*'),
      supabase.from(TABLES.EVENTS).select('*'),
      supabase.from(TABLES.CONTACT_INFO).select('*').single()
    ]);

    // Check for errors
    const errors = [settingsResult, sectionsResult, itemsResult, hoursResult, eventsResult, contactResult]
      .map(result => result.error)
      .filter(Boolean);

    if (errors.length > 0) {
      throw new Error(`Database errors: ${errors.map(e => e.message).join(', ')}`);
    }

    // Transform to match existing JSON structure
    const data = transformDBDataToJSON({
      settings: settingsResult.data,
      sections: sectionsResult.data,
      items: itemsResult.data,
      hours: hoursResult.data,
      events: eventsResult.data,
      contact: contactResult.data
    });

    return { success: true, data };
  } catch (error) {
    console.error('Error fetching hotel data from database:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Transform database data to match existing JSON structure
 */
const transformDBDataToJSON = ({ settings, sections, items, hours, events, contact }) => {
  // Group menu items by section
  const menuItems = {};
  const sectionOrder = [];
  const sectionNames = {};

  // Build sections structure
  sections.forEach(section => {
    menuItems[section.key] = [];
    sectionOrder.push(section.key);
    sectionNames[section.key] = section.name;
  });

  // Add items to their sections
  items.forEach(item => {
    if (menuItems[item.section_key]) {
      menuItems[item.section_key].push({
        id: item.item_id,
        name: item.name,
        description: item.description,
        price: item.price,
        available: item.available
      });
    }
  });

  // Group opening hours by venue type
  const openingHours = {};
  hours.forEach(hour => {
    if (!openingHours[hour.venue_type]) {
      openingHours[hour.venue_type] = [];
    }
    openingHours[hour.venue_type].push({
      day: hour.day_of_week,
      hours: hour.hours
    });
  });

  // Transform events
  const transformedEvents = events.map(event => ({
    id: event.event_id,
    title: event.title,
    day: event.day,
    time: event.time,
    description: event.description,
    active: event.active
  }));

  // Build final structure
  return {
    lastUpdated: settings?.last_updated || new Date().toISOString(),
    version: settings?.version || '1.0.0',
    openingHours,
    events: transformedEvents,
    menuItems,
    menuSectionOrder: sectionOrder,
    menuSectionNames: sectionNames,
    contactInfo: contact ? {
      phone: contact.phone,
      email: contact.email,
      address: {
        name: contact.address_name,
        street: contact.address_street,
        city: contact.address_city,
        postcode: contact.address_postcode
      }
    } : null,
    socialMedia: contact ? {
      facebook: contact.facebook_url,
      booking: contact.booking_url
    } : null
  };
};

/**
 * Update menu items in database
 */
export const updateMenuItemsInDB = async (menuItems) => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Supabase not configured' };
  }

  try {
    // First, delete all existing menu items
    const { error: deleteError } = await supabase
      .from(TABLES.MENU_ITEMS)
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all

    if (deleteError) throw deleteError;

    // Prepare new items for insertion
    const itemsToInsert = [];
    Object.entries(menuItems).forEach(([sectionKey, items]) => {
      items.forEach(item => {
        itemsToInsert.push({
          item_id: item.id,
          section_key: sectionKey,
          name: item.name,
          description: item.description,
          price: item.price,
          available: item.available
        });
      });
    });

    // Insert new items
    if (itemsToInsert.length > 0) {
      const { error: insertError } = await supabase
        .from(TABLES.MENU_ITEMS)
        .insert(itemsToInsert);

      if (insertError) throw insertError;
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating menu items in database:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Replace all menu data (sections + items) in a single sequential operation.
 * Handles new sections, deleted sections, renamed sections, and changed items.
 * Sequential order matters: items must be deleted before sections (FK safety),
 * and sections must be inserted before items.
 */
export const replaceAllMenuDataInDB = async (menuItems, sectionOrder, sectionNames) => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Supabase not configured' };
  }

  const defaultSectionNames = {
    starters: 'Starters',
    pubClassics: 'Pub Classics',
    curries: 'Curries',
    fromTheSea: 'From the Sea',
    burgers: 'Burgers',
    desserts: 'Desserts',
    sides: 'Sides',
    drinks: 'Drinks'
  };

  try {
    // 1. Delete all items first (before sections, in case of FK constraint)
    const { error: deleteItemsError } = await supabase
      .from(TABLES.MENU_ITEMS)
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000');
    if (deleteItemsError) throw deleteItemsError;

    // 2. Delete all sections
    const { error: deleteSectionsError } = await supabase
      .from(TABLES.MENU_SECTIONS)
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000');
    if (deleteSectionsError) throw deleteSectionsError;

    // 3. Insert sections
    const sectionsToInsert = sectionOrder.map((key, index) => ({
      key,
      name: sectionNames[key] || defaultSectionNames[key] || key,
      display_order: index
    }));

    if (sectionsToInsert.length > 0) {
      const { error: insertSectionsError } = await supabase
        .from(TABLES.MENU_SECTIONS)
        .insert(sectionsToInsert);
      if (insertSectionsError) throw insertSectionsError;
    }

    // 4. Insert items (sections now exist, so section_key references are valid)
    const itemsToInsert = [];
    sectionOrder.forEach(sectionKey => {
      (menuItems[sectionKey] || []).forEach(item => {
        itemsToInsert.push({
          item_id: item.id,
          section_key: sectionKey,
          name: item.name,
          description: item.description,
          price: item.price,
          available: item.available
        });
      });
    });

    if (itemsToInsert.length > 0) {
      const { error: insertItemsError } = await supabase
        .from(TABLES.MENU_ITEMS)
        .insert(itemsToInsert);
      if (insertItemsError) throw insertItemsError;
    }

    return { success: true };
  } catch (error) {
    console.error('Error replacing menu data in database:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update menu section order in database
 */
export const updateMenuSectionOrderInDB = async (sectionOrder) => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Supabase not configured' };
  }

  try {
    // Update display_order for each section
    const updates = sectionOrder.map((sectionKey, index) => 
      supabase
        .from(TABLES.MENU_SECTIONS)
        .update({ display_order: index })
        .eq('key', sectionKey)
    );

    const results = await Promise.all(updates);
    const errors = results.map(r => r.error).filter(Boolean);

    if (errors.length > 0) {
      throw new Error(`Failed to update section order: ${errors.map(e => e.message).join(', ')}`);
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating section order in database:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update menu section names in database
 */
export const updateMenuSectionNamesInDB = async (sectionNames) => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Supabase not configured' };
  }

  try {
    const updates = Object.entries(sectionNames).map(([sectionKey, name]) =>
      supabase
        .from(TABLES.MENU_SECTIONS)
        .update({ name })
        .eq('key', sectionKey)
    );

    const results = await Promise.all(updates);
    const errors = results.map(r => r.error).filter(Boolean);

    if (errors.length > 0) {
      throw new Error(`Failed to update section names: ${errors.map(e => e.message).join(', ')}`);
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating section names in database:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update opening hours in database
 */
export const updateOpeningHoursInDB = async (openingHours) => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Supabase not configured' };
  }

  try {
    // Delete existing hours
    const { error: deleteError } = await supabase
      .from(TABLES.OPENING_HOURS)
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all

    if (deleteError) throw deleteError;

    // Prepare new hours for insertion
    const hoursToInsert = [];
    Object.entries(openingHours).forEach(([venueType, hours]) => {
      hours.forEach(hour => {
        hoursToInsert.push({
          venue_type: venueType,
          day_of_week: hour.day,
          hours: hour.hours
        });
      });
    });

    // Insert new hours
    if (hoursToInsert.length > 0) {
      const { error: insertError } = await supabase
        .from(TABLES.OPENING_HOURS)
        .insert(hoursToInsert);

      if (insertError) throw insertError;
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating opening hours in database:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update events in database
 */
export const updateEventsInDB = async (events) => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Supabase not configured' };
  }

  try {
    // Delete existing events
    const { error: deleteError } = await supabase
      .from(TABLES.EVENTS)
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all

    if (deleteError) throw deleteError;

    // Prepare new events for insertion
    const eventsToInsert = events.map(event => ({
      event_id: event.id,
      title: event.title,
      day: event.day,
      time: event.time,
      description: event.description,
      active: event.active
    }));

    // Insert new events
    if (eventsToInsert.length > 0) {
      const { error: insertError } = await supabase
        .from(TABLES.EVENTS)
        .insert(eventsToInsert);

      if (insertError) throw insertError;
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating events in database:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Check if database is properly initialized
 */
export const isDatabaseInitialized = async () => {
  if (!isSupabaseConfigured()) {
    return false;
  }

  try {
    const { data, error } = await supabase
      .from(TABLES.HOTEL_SETTINGS)
      .select('id')
      .limit(1);

    return !error && data !== null;
  } catch (error) {
    return false;
  }
};